import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Eye, 
  Users, 
  Calendar, 
  TrendingUp,
  FileText,
  Mail,
  Bell,
  UserCircle,
  Car,
  UserPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div data-testid="admin-analytics">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-8">ANALYTICS</h1>
        <p className="text-[#A19D98]">Loading analytics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div data-testid="admin-analytics">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-8">ANALYTICS</h1>
        <p className="text-[#A19D98]">Failed to load analytics.</p>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Page Views', value: stats.total_views, icon: Eye, color: '#E8651A' },
    { label: 'Unique Visitors', value: stats.unique_visitors, icon: Users, color: '#34C759' },
    { label: 'Views Today', value: stats.views_today, icon: Calendar, color: '#007AFF' },
    { label: 'Views This Week', value: stats.views_this_week, icon: TrendingUp, color: '#FF9500' },
    { label: 'Views This Month', value: stats.views_this_month, icon: TrendingUp, color: '#AF52DE' },
  ];

  const conversionCards = [
    { label: 'Rally Signups', value: stats.signups_count, icon: FileText, color: '#E8651A' },
    { label: 'Sponsor Inquiries', value: stats.inquiries_count, icon: Mail, color: '#34C759' },
    { label: 'Notify Subscribers', value: stats.notify_count, icon: Bell, color: '#007AFF' },
  ];

  const memberCards = [
    { label: 'Total Members', value: stats.members_count || 0, icon: UserCircle, color: '#E8651A' },
    { label: 'New This Week', value: stats.new_members_this_week || 0, icon: UserPlus, color: '#34C759' },
    { label: 'New This Month', value: stats.new_members_this_month || 0, icon: UserPlus, color: '#007AFF' },
    { label: 'Vehicles in Garage', value: stats.vehicles_count || 0, icon: Car, color: '#AF52DE' },
  ];

  // Calculate max views for chart scaling
  const maxDailyViews = Math.max(...(stats.daily_views.map(d => d.views) || [1]), 1);

  return (
    <div data-testid="admin-analytics">
      <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-8">ANALYTICS</h1>

      {/* Traffic Stats */}
      <div className="mb-12">
        <h2 className="font-['Oswald'] text-xl text-[#A19D98] mb-4 tracking-wider">
          TRAFFIC
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((stat, i) => (
            <div
              key={i}
              className="bg-[#141414] border border-[#2A2A2A] p-6"
              data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <p className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-1">
                {stat.value.toLocaleString()}
              </p>
              <p className="text-[#A19D98] text-xs">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Conversions */}
      <div className="mb-12">
        <h2 className="font-['Oswald'] text-xl text-[#A19D98] mb-4 tracking-wider">
          CONVERSIONS
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {conversionCards.map((stat, i) => (
            <div
              key={i}
              className="bg-[#141414] border border-[#2A2A2A] p-6"
            >
              <div className="flex items-center justify-between mb-3">
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <p className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-1">
                {stat.value.toLocaleString()}
              </p>
              <p className="text-[#A19D98] text-xs">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Members */}
      <div className="mb-12" data-testid="analytics-members-section">
        <h2 className="font-['Oswald'] text-xl text-[#A19D98] mb-4 tracking-wider">
          MEMBERS & GARAGE
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {memberCards.map((stat, i) => (
            <div
              key={i}
              className="bg-[#141414] border border-[#2A2A2A] p-6"
              data-testid={`analytics-member-${stat.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <p className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-1">
                {stat.value.toLocaleString()}
              </p>
              <p className="text-[#A19D98] text-xs">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Views Chart */}
      <div className="mb-12">
        <h2 className="font-['Oswald'] text-xl text-[#A19D98] mb-4 tracking-wider">
          DAILY VIEWS (LAST 14 DAYS)
        </h2>
        <div className="bg-[#141414] border border-[#2A2A2A] p-6">
          {stats.daily_views.length === 0 ? (
            <p className="text-[#A19D98] text-center py-8">No data yet. Views will appear here once visitors start coming!</p>
          ) : (
            <div className="flex items-end gap-2 h-48">
              {stats.daily_views.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-[#E8651A] rounded-t transition-all hover:bg-[#FF7A2F]"
                    style={{ 
                      height: `${(day.views / maxDailyViews) * 100}%`,
                      minHeight: day.views > 0 ? '4px' : '0'
                    }}
                    title={`${day.date}: ${day.views} views`}
                  />
                  <p className="text-[#A19D98] text-[10px] mt-2 rotate-45 origin-left">
                    {day.date.slice(5)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Page Breakdown */}
      <div>
        <h2 className="font-['Oswald'] text-xl text-[#A19D98] mb-4 tracking-wider">
          PAGES BY VIEWS
        </h2>
        <div className="bg-[#141414] border border-[#2A2A2A]">
          {Object.keys(stats.page_breakdown).length === 0 ? (
            <p className="text-[#A19D98] text-center py-8">No page views recorded yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Views</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.page_breakdown).map(([page, views], i) => (
                  <tr key={i}>
                    <td className="text-[#F2EDE6]">{page}</td>
                    <td className="text-[#A19D98]">{views.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
